import { ENVIRONMENT_BY_KEY_LETTER, type Environment } from '@zelisight/contract';
import { CircleAlertIcon, ShieldOffIcon } from 'lucide-react';

import { getConfig } from '@/config';
import type { WriteKey } from '@/api/schemas';

/** A hostname that is plainly a developer machine, not a deployed site. */
function isLocalHost(hostname: string): boolean {
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '[::1]' ||
    hostname === '0.0.0.0' ||
    hostname.endsWith('.local') ||
    hostname.endsWith('.localhost') ||
    hostname.endsWith('.test')
  );
}

export interface MismatchedKey {
  key: WriteKey;
  origin: string;
  env: Environment;
}

/**
 * Detect the single most common self inflicted onboarding failure: a
 * development or staging write key embedded in a build that is serving a real
 * hostname. Plan Phase 3 calls this out explicitly, and the observed origin on
 * the key item is what makes it detectable rather than guesswork.
 *
 * The environment is read from the key prefix letter rather than from the
 * key's `env` field, so this stays right even if the two ever disagree: the
 * letter is what the collector actually routes on.
 */
export function findEnvironmentMismatch(keys: readonly WriteKey[]): MismatchedKey | null {
  for (const key of keys) {
    const letter = key.key_prefix.charAt(3);
    const env =
      letter in ENVIRONMENT_BY_KEY_LETTER
        ? ENVIRONMENT_BY_KEY_LETTER[letter as keyof typeof ENVIRONMENT_BY_KEY_LETTER]
        : key.env;
    if (env === 'production') continue;

    for (const origin of key.last_seen_origins) {
      let hostname: string;
      try {
        hostname = new URL(origin).hostname;
      } catch {
        continue;
      }
      if (!isLocalHost(hostname)) return { key, origin, env };
    }
  }
  return null;
}

/**
 * The ten minute escape hatch.
 *
 * Plan Phase 3 calls this "the difference between a converted trial and a
 * churned one", and the reasoning is worth keeping next to the code: after ten
 * minutes of nothing, the customer's next move is either to debug or to close
 * the tab, so this panel has to answer "is it you or is it us" without asking
 * them to open a ticket.
 *
 * Plan Phase 3 answers it with a server side synthetic event. That button is
 * not here, and its absence is deliberate rather than unfinished. The collector
 * accepts exactly one credential, a write key, and a write key's plaintext is
 * returned once at creation and never stored, so no server side component holds
 * one to send on a tenant's behalf. Adding the button needs a system
 * authenticated ingest route on the one endpoint in this product that is
 * unauthenticated and public by design, which is a security decision to be
 * taken deliberately, not a wire up.
 *
 * What is here still answers the question, just by elimination rather than by
 * demonstration: the environment mismatch detector catches the single most
 * common cause outright, and the checklist below orders the rest by how often
 * each one is actually the answer. See docs/STATUS.md for the open item.
 */
export function OnboardingEscapeHatch({ mismatch }: { mismatch: MismatchedKey | null }) {
  const config = getConfig();

  return (
    <div className="space-y-4 rounded-xl border border-warning/40 bg-warning/5 p-5">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full bg-warning/15">
          <CircleAlertIcon className="size-4 text-warning" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">Still nothing after ten minutes</p>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Nothing has reached us from this key yet. In our experience it is one of the three
            things below, in that order, and the first one is worth checking before anything else.
          </p>
        </div>
      </div>

      {mismatch ? (
        <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 p-3">
          <ShieldOffIcon className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden />
          <p className="text-sm">
            <span className="font-medium">
              A {mismatch.env} key is running on a live hostname.
            </span>{' '}
            Key <code className="font-mono text-xs">{mismatch.key.key_prefix}</code> was last seen
            from <code className="font-mono text-xs">{mismatch.origin}</code>. Its data lands in{' '}
            {mismatch.env}, not production, which is why the production view looks empty. A key&rsquo;s
            environment is fixed when it is created, so swap the key rather than changing a setting.
          </p>
        </div>
      ) : null}

      <div>
        <p className="text-sm font-medium">Check these three, in order</p>
        <ol className="mt-2 space-y-2 text-sm text-muted-foreground">
          <li className="flex gap-2">
            <span className="text-foreground">1.</span>
            <span>
              <span className="font-medium text-foreground">A content blocker.</span> Open your
              network panel and look for a request to{' '}
              <code className="font-mono text-xs">{config.ingestHost}/v1/e</code>. If it is not
              there at all, something in the browser removed it before it was sent. Our hostname
              avoids the usual blocked words, but a strict list still catches new domains.
            </span>
          </li>
          <li className="flex gap-2">
            <span className="text-foreground">2.</span>
            <span>
              <span className="font-medium text-foreground">The environment key.</span> A key is
              pinned to one environment for its whole life. A development key sends real events
              that land in development, so production stays empty and nothing looks broken.
            </span>
          </li>
          <li className="flex gap-2">
            <span className="text-foreground">3.</span>
            <span>
              <span className="font-medium text-foreground">CORS or a proxy.</span> If the request
              is sent but fails, the response will say so. The uncompressed path is a simple
              request with no preflight, so a failure here usually means a corporate proxy rather
              than our configuration.
            </span>
          </li>
        </ol>
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium">The long term answer to blocking</p>
        <p className="text-sm text-muted-foreground">
          Content blockers work on hostnames, so the only measure that keeps working is serving
          ingest from your own origin. Point <code className="font-mono text-xs">api_host</code> at
          a path on your domain and proxy it through:
        </p>
        <pre className="overflow-x-auto rounded-lg border border-border bg-muted/50 p-3 font-mono text-xs leading-relaxed">
          <code>{`zelisight.init(YOUR_KEY, { api_host: "https://your-app.example/zs" });

# then proxy /zs/* to ${config.ingestHost}/*
# nginx:  location /zs/ { proxy_pass ${config.ingestHost}/; }`}</code>
        </pre>
        <p className="text-xs text-muted-foreground">
          The full recipe, with the Vite dev proxy, the Next.js rewrite, a Cloudflare Worker and
          the complete nginx block, ships with the SDK as{' '}
          <code className="font-mono">docs/reverse-proxy.md</code>.
        </p>
      </div>
    </div>
  );
}
